import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ScreenShell } from "../components/ScreenShell";
import { MacroBars } from "../components/MacroBars";
import { AnimatedLineChart } from "../components/AnimatedLineChart";
import type { FlowAnswers } from "../types";
import { computePlan } from "../plan";

interface InterstitialShellProps {
  readonly image: string;
  readonly imageAlt: string;
  readonly headline: string;
  readonly body?: string;
  readonly visual?: ReactNode;
  readonly onContinue: () => void;
  readonly ctaLabel?: string;
}

function InterstitialShell({
  image,
  imageAlt,
  headline,
  body,
  visual,
  onContinue,
  ctaLabel = "Continue",
}: InterstitialShellProps) {
  return (
    <ScreenShell
      center
      footer={
        <Button variant="primary" size="lg" className="w-full" onClick={onContinue}>
          {ctaLabel}
        </Button>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <img
            src={image}
            alt={imageAlt}
            className="h-full w-full rounded-3xl border border-border object-contain shadow-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-xl font-bold leading-tight text-text">{headline}</h1>
          {body && <p className="text-sm leading-snug text-text-muted">{body}</p>}
        </div>
        {visual}
      </div>
    </ScreenShell>
  );
}

interface BenefitOneScreenProps {
  readonly answers: FlowAnswers;
  readonly onContinue: () => void;
}

export function BenefitOneScreen({ answers, onContinue }: BenefitOneScreenProps) {
  const preview = computePlan({
    goalType: answers.goalType,
    weightKg: answers.weightKg,
    heightCm: answers.heightCm,
    age: answers.age,
    gender: answers.gender,
    activityLevel: answers.activityLevel,
    exerciseFrequency: answers.exerciseFrequency,
    exerciseType: answers.exerciseType,
    avgDailySteps: answers.avgDailySteps,
    eatingStyle: answers.eatingStyle,
    bodyFatPercent: answers.bodyFatPercent,
    waistCm: answers.waistCm,
    safetyFlags: answers.safetyFlags,
  });

  return (
    <InterstitialShell
      image="/onboarding/scan.png"
      imageAlt="Phone scanning a plate of food"
      headline="See every meal clearly, instantly"
      body="Snap a photo and BiteTrack's AI breaks down calories and macros in seconds — no searching endless food databases."
      visual={
        <MacroBars proteinG={preview.proteinG} carbsG={preview.carbsG} fatG={preview.fatG} />
      }
      onContinue={onContinue}
    />
  );
}

interface BenefitTwoScreenProps {
  readonly answers: FlowAnswers;
  readonly onContinue: () => void;
}

export function BenefitTwoScreen({ answers, onContinue }: BenefitTwoScreenProps) {
  const plan = computePlan({
    goalType: answers.goalType,
    weightKg: answers.weightKg,
    targetWeightKg: answers.targetWeightKg,
    heightCm: answers.heightCm,
    age: answers.age,
    gender: answers.gender,
    activityLevel: answers.activityLevel,
    exerciseFrequency: answers.exerciseFrequency,
    exerciseType: answers.exerciseType,
    avgDailySteps: answers.avgDailySteps,
    weightLossPace: answers.weightLossPace,
    eatingStyle: answers.eatingStyle,
    bodyFatPercent: answers.bodyFatPercent,
    waistCm: answers.waistCm,
    safetyFlags: answers.safetyFlags,
  });
  const startKg = answers.weightKg ?? 80;
  const goalKg = answers.targetWeightKg ?? (answers.goalType === "gain" || answers.goalType === "muscle" ? startKg + 6 : startKg - 8);
  const weeks = plan.weeksToGoal > 0 ? plan.weeksToGoal : 12;

  return (
    <InterstitialShell
      image="/onboarding/progress.png"
      imageAlt="Weight trend chart on a phone"
      headline="Your progress, visualized"
      body="Most BiteTrack users see a clear trend within 2 weeks of consistent logging."
      visual={
        <AnimatedLineChart startKg={startKg} goalKg={goalKg} weeks={weeks} unit={answers.unitWeight} />
      }
      onContinue={onContinue}
    />
  );
}

const ACCURACY_BULLETS = [
  "AI gives you a fast starting estimate",
  "You can correct portions anytime",
  "Your plan improves as you log more meals and weigh-ins",
] as const;

export function AccuracyScreen({ onContinue }: { readonly onContinue: () => void }) {
  return (
    <InterstitialShell
      image="/onboarding/plan.png"
      imageAlt="Personalised plan summary"
      headline="The more honestly you log, the better your plan works"
      visual={
        <ul className="flex flex-col gap-3">
          {ACCURACY_BULLETS.map((bullet) => (
            <li key={bullet} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-light text-accent">
                <Check size={14} strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="text-sm leading-relaxed text-text">{bullet}</span>
            </li>
          ))}
        </ul>
      }
      onContinue={onContinue}
    />
  );
}

const SOCIAL_BENEFITS = [
  "AI meal logging",
  "Calorie + macro tracking",
  "Weight trend tracking",
  "Personalised recommendations",
  "Progress dashboard",
] as const;

export function SocialProofScreen({ onContinue }: { readonly onContinue: () => void }) {
  return (
    <InterstitialShell
      image="/onboarding/celebrate.png"
      imageAlt="Person celebrating progress"
      headline="Most users succeed when they stay consistent for 2+ weeks"
      body="People who log meals regularly lose more weight than those who guess."
      visual={
        <ul className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4">
          {SOCIAL_BENEFITS.map((benefit) => (
            <li key={benefit} className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-light text-accent">
                <Check size={14} strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="text-sm font-medium text-text">{benefit}</span>
            </li>
          ))}
        </ul>
      }
      onContinue={onContinue}
    />
  );
}
